import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { isWaived } from '../../../shared/constant/schoolFees';

const peso = (value) => `${Number(value) < 0 ? '-' : ''}₱${Math.abs(Number(value)).toLocaleString()}`;

function PaymentReviewModal({ rows, onBack, onConfirm }) {
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const total = rows.reduce((sum, r) => sum + Number(r.amount), 0);
  const first = rows[0];
  const isFull = first.status === 'Full';

  const handleConfirm = async () => {
    setError('');
    setSaving(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err.message || 'Failed to save payment');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl max-h-[70vh] overflow-hidden flex flex-col">
        <div className="shrink-0 flex items-center justify-between px-5 py-4 bg-navy-light">
          <h2 className="text-lg font-semibold text-white">Review Payment</h2>
          <button type="button" onClick={onBack} className="p-1 text-gray-200 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{error}</p>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Payer</p>
              <p className="font-medium text-gray-900">{first.payer_name}</p>
            </div>
            <div>
              <p className="text-gray-500">Grade Level</p>
              <p className="font-medium text-gray-900">{first.grade_level}</p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <p className="font-medium text-gray-900">{first.status}</p>
            </div>
            <div>
              <p className="text-gray-500">Payment Date</p>
              <p className="font-medium text-gray-900">{first.payment_date}</p>
            </div>
            <div>
              <p className="text-gray-500">OR Number</p>
              <p className="font-medium text-gray-900">{first.or_number || '—'}</p>
            </div>
            {first.remarks && (
              <div>
                <p className="text-gray-500">Remarks</p>
                <p className="font-medium text-gray-900">{first.remarks}</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm text-gray-500 mb-2">
              {rows.length === 1 ? '1 line item' : `${rows.length} line items`}
            </p>
            <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
              {rows.map((row, index) => (
                <div key={`${row.category}-${index}`} className="flex justify-between px-3 py-2 text-sm">
                  <span className="text-gray-700">{isWaived(row) ? 'PTA — waived (discount)' : row.category}</span>
                  <span className="font-medium text-gray-900">{peso(row.amount)}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`flex justify-between items-center px-4 py-3 rounded-lg text-xl font-semibold ${
              isFull ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning-dark'
            }`}
          >
            <span>Total</span>
            <span>{peso(total)}</span>
          </div>
        </div>

        <div className="shrink-0 flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            className="w-28 py-2 text-center text-sm font-medium text-gray-600 border border-gray-300 hover:text-gray-900 disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving}
            className="w-32 py-2 rounded-md bg-navy-light text-white text-sm font-medium text-center hover:bg-navy disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Confirm & Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentReviewModal;
